import { useApolloClient, useQuery } from '@apollo/client';
import React from 'react';
import { AsyncSelect } from '../ReactSelect';
import { ProfileType } from '../Profile/types';
import { gql } from '@/modes/company/gql';

type User = {
  profileId: string;
  profile: {
    id: string;
    name: string;
    firstName: string;
    lastName: string;
    type: ProfileType;
  };
};

type UserPickerProps = {
  userId?: string | null;
  // where?: any;
  companies?: boolean;
  onChange?: (tester: User | null) => void;
  placeholder?: string;
  isClearable?: boolean;
  autoFocus?: boolean;
  isDisabled?: boolean;
};

const GET_USERS = gql(`
  query GetUsers(
    $where: JSON
    $orderBy: [ColumnOrder]
    $limit: Int
    $offset: Int
  ) {
    users(where: $where, orderBy: $orderBy, limit: $limit, offset: $offset) {
      profileId
      profile {
        id
        type
        name
        firstName
        lastName
      }
    }
  }
`);

const UserPicker: React.FC<UserPickerProps> = ({
  userId,
  companies = false,
  // where,
  onChange,
  placeholder,
  isClearable,
  autoFocus,
  isDisabled,
}) => {
  const client = useApolloClient();

  const { error: itemError, data: itemData } = useQuery<{
    users: User[];
  }>(GET_USERS, {
    variables: {
      where: {
        profileId: userId,
        status: 'ACTIVE',
      },
    },
    skip: !userId,
  });

  const selected: User | undefined = itemData?.users[0];

  const loadOptionsFunc = async (inputValue: string) => {
    const res = await client.query<{
      users: User[];
    }>({
      query: GET_USERS,
      variables: {
        where: !companies
          ? {
              '$profile.type$': 1,
            }
          : {},
        limit: null,
      },
      fetchPolicy: 'network-only',
    });

    if (res.data && res.data.users) {
      if (!selected && res.data.users.length === 1) {
        onChange?.(res.data.users[0]);
      }
      const companies: User[] = [];
      const individuals: User[] = [];

      const filtered = res.data.users.filter(
        (user) =>
          !inputValue ||
          user.profile.name.toLowerCase().includes(inputValue.toLowerCase())
      );

      filtered.forEach((user: User) => {
        if (user.profile.type === 'COMPANY') {
          companies.push(user);
        } else {
          individuals.push(user);
        }
      });

      if (companies.length > 0 && individuals.length > 0) {
        return [
          {
            label: 'Individuals',
            options: individuals,
          },
          {
            label: 'Companies',
            options: companies,
          },
        ];
      }

      return filtered;
    }

    return [];
  };

  return (
    <>
      <AsyncSelect
        autoFocus={autoFocus}
        cacheOptions
        value={selected}
        loadOptions={loadOptionsFunc}
        defaultOptions
        isDisabled={isDisabled}
        isClearable={isClearable}
        noOptionsMessage={() => null}
        menuPlacement="auto"
        styles={{
          menu: (base) => ({
            ...base,
            minWidth: '300px',
          }),
        }}
        onChange={(selected: User | null) => {
          if (!selected) {
            onChange?.(null);
          } else {
            onChange?.(selected);
          }
        }}
        placeholder={placeholder ?? 'Enter user name...'}
        getOptionLabel={(user) =>
          user.profile.type === 'COMPANY'
            ? user.profile.name
            : `${user.profile.firstName} ${user.profile.lastName}`
        }
        getOptionValue={(tester) => tester.profileId}
      />
    </>
  );
};

export default UserPicker;
